import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  BadgeOutlined as RoleIcon,
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  FileDownload as ExportIcon,
  CheckCircleOutline as SuccessIcon,
  ErrorOutline as ErrorIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../apiBase';
import { useAuth } from '../context/AuthContext';

export default function RoleMaster() {
  const { user } = useAuth();
  const role = user?.role || '';
  const isSuperAdmin = role === 'superadmin';
  const permissionsMap = user?.page_permissions_map || {};
  const rolePerm = permissionsMap.role_master || {};
  const canCreate = isSuperAdmin || Boolean(rolePerm.can_create);
  const canUpdate = isSuperAdmin || Boolean(rolePerm.can_update);
  const canDelete = isSuperAdmin || Boolean(rolePerm.can_delete);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [roleName, setRoleName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastSeverity, setToastSeverity] = useState('success');
  const [toastMessage, setToastMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const isEditing = useMemo(() => Boolean(editingId), [editingId]);

  const showToast = (severity, message) => {
    setToastSeverity(severity);
    setToastMessage(message);
    setToastOpen(true);
  };

  useEffect(() => {
    const fetchRoles = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/roles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load roles');
        setRoles(data.roles || []);
      } catch {
        setRoles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, [token]);

  const handleSubmit = async () => {
    const trimmed = roleName.trim();
    if (!trimmed) {
      showToast('error', 'Role name is required');
      return;
    }
    if (editingId && !canUpdate) {
      showToast('error', 'You do not have update permission for this page');
      return;
    }
    if (!editingId && !canCreate) {
      showToast('error', 'You do not have create permission for this page');
      return;
    }
    if (!token) {
      showToast('error', 'You are not logged in');
      return;
    }
    setSaving(true);
    try {
      const url = editingId ? `${API_BASE_URL}/api/roles/${editingId}` : `${API_BASE_URL}/api/roles`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ role_name: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to create role');
      }
      if (data.role) {
        if (editingId) {
          setRoles((prev) =>
            prev.map((r) => (r.id === editingId ? { ...r, ...data.role } : r))
          );
        } else {
          setRoles((prev) => [data.role, ...prev]);
        }
      }
      showToast('success', editingId ? 'Role updated' : 'Role created');
      setRoleName('');
      setEditingId(null);
      setPage(0);
    } catch (e) {
      showToast('error', e.message || 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (role) => {
    setRoleName(role.role_name || '');
    setEditingId(role.id);
  };

  const handleDeleteClick = (role) => {
    if (!canDelete) {
      showToast('error', 'You do not have delete permission for this page');
      return;
    }
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    if (deletingId) return;
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!canDelete) {
      showToast('error', 'You do not have delete permission for this page');
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
      return;
    }
    if (!roleToDelete) {
      setDeleteDialogOpen(false);
      return;
    }
    if (!token) {
      showToast('error', 'You are not logged in');
      setDeleteDialogOpen(false);
      return;
    }

    setDeletingId(roleToDelete.id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/roles/${roleToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to delete role');
      }
      setRoles((prev) => prev.filter((r) => r.id !== roleToDelete.id));
      if (editingId === roleToDelete.id) {
        setEditingId(null);
        setRoleName('');
      }
      showToast('success', 'Role deleted');
    } catch (e) {
      showToast('error', e.message || 'Failed to delete role');
    } finally {
      setDeletingId(null);
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    }
  };

  const filteredRoles = roles.filter((role) =>
    role.role_name?.toLowerCase().includes(search.toLowerCase()) ||
    String(role.id).includes(search)
  );

  const paginatedRoles = filteredRoles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const rowsHtml = filteredRoles
      .map(
        (r) =>
          `<tr><td>${r.id}</td><td>${r.role_name || ''}</td></tr>`
      )
      .join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>Role List</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #e5e7eb; padding: 8px; text-align: left; }
            th { background: #f3f4f6; }
          </style>
        </head>
        <body>
          <h3>Role List</h3>
          <table>
            <thead>
              <tr><th>ID</th><th>Role Name</th></tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleExportCsv = () => {
    const header = ['ID', 'Role Name'];
    const rows = filteredRoles.map((r) => [r.id, r.role_name || '']);
    const csvContent = [header, ...rows]
      .map((row) =>
        row
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(',')
      )
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'roles.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          alignItems: 'stretch',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          maxWidth: 1400,
          mx: 'auto',
        }}
      >
        {/* Left: Add / Edit Role */}
        <Box sx={{ width: { xs: '100%', md: 460 }, flexShrink: 0 }}>
          <Paper
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 18px 45px rgba(15,23,42,0.08)',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(124,58,237,0.10)',
                  color: 'secondary.main',
                }}
              >
                <AddIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 500, fontSize: 18, lineHeight: 1.2 }}>
                  {isEditing ? 'Edit Role' : 'Add New Role'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {isEditing ? 'Update role details' : 'Create a new role'}
                </Typography>
              </Box>
            </Box>
            <Divider />

            <Box sx={{ p: 2.5 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500, mb: 1 }}>
                Role Name <span style={{ color: '#ef4444' }}>*</span>
              </Typography>
              <TextField
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="Enter role name"
                variant="outlined"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <RoleIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(15,23,42,0.5)'
                        : 'rgba(249,250,251,1)',
                  },
                }}
              />

              <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                {isEditing && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setEditingId(null);
                      setRoleName('');
                    }}
                    disabled={saving}
                    sx={{ minWidth: 140 }}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={saving}
                  fullWidth
                  sx={{ height: 46 }}
                >
                  {saving ? (isEditing ? 'Updating...' : 'Saving...') : isEditing ? 'Update Role' : 'Create Role'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Right: Roles List */}
        <Box sx={{ width: { xs: '100%', md: 'auto' }, flexGrow: 1, minWidth: 0 }}>
          <Paper
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 18px 45px rgba(15,23,42,0.08)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                p: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 500, fontSize: 18 }}>Roles List</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {filteredRoles.length} roles
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {/* buttons removed as requested */}
              </Box>
            </Box>

            <Divider />

            <Box sx={{ p: 2.5, pt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Search roles"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 260,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(31,41,55,1)'
                          : 'rgba(243,244,246,1)',
                      '& fieldset': { borderColor: 'transparent' },
                      '&:hover fieldset': { borderColor: 'transparent' },
                    },
                  }}
                />

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton onClick={handleExportCsv}>
                    <ExportIcon fontSize="small" />
                  </IconButton>
                  <IconButton onClick={handlePrint}>
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontSize: 12,
                          textTransform: 'uppercase',
                          color: 'text.secondary',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          width: 80,
                        }}
                      >
                        ID
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontSize: 12,
                          textTransform: 'uppercase',
                          color: 'text.secondary',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        Role Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          fontSize: 12,
                          textTransform: 'uppercase',
                          color: 'text.secondary',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          width: 140,
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRoles.map((role) => (
                      <TableRow
                        key={role.id}
                        hover
                        sx={{ '&:last-of-type td, &:last-of-type th': { borderBottom: 'none' } }}
                      >
                        <TableCell>{role.id}</TableCell>
                        <TableCell>{role.role_name}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleEditClick(role)} sx={{ mr: 0.5 }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(role)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!loading && paginatedRoles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ color: 'text.secondary' }}>
                          No roles found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                rowsPerPageOptions={[5, 10, 25]}
                count={filteredRoles.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete role</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Are you sure you want to delete role
            {roleToDelete?.role_name ? ` "${roleToDelete.role_name}"` : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={!!deletingId}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={!!deletingId}
          >
            {deletingId ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={toastOpen}
        autoHideDuration={2500}
        onClose={() => setToastOpen(false)}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            boxShadow: 3,
            bgcolor: (theme) =>
              toastSeverity === 'success'
                ? theme.palette.mode === 'dark'
                  ? 'success.dark'
                  : 'success.main'
                : theme.palette.mode === 'dark'
                ? 'error.dark'
                : 'error.main',
            color: 'common.white',
            maxWidth: 360,
          }}
        >
          <Box sx={{ mt: 0.2 }}>
            {toastSeverity === 'success' ? (
              <SuccessIcon sx={{ fontSize: 20 }} />
            ) : (
              <ErrorIcon sx={{ fontSize: 20 }} />
            )}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {toastMessage}
          </Typography>
        </Box>
      </Snackbar>
    </Layout>
  );
}
